import { Observable, combineLatest, distinctUntilChanged, map } from 'rxjs';
import { range, uniqBy, isEqual } from 'lodash';
import { isTruthy } from '@ardoq/common-helpers';
import { computeLegendComponentTypes } from './legendComponentTypes';
import type {
  ContextShape,
  RepresentationData,
  WorkspaceAndModelLookup,
} from '@ardoq/data-model';
import {
  APIComponentAttributes,
  APIComponentType,
  APIFieldAttributes,
  APIFieldType,
  APIReferenceAttributes,
  ArdoqId,
  LinkedComponent,
  ViewIds,
} from '@ardoq/api-types';
import {
  componentInterface,
  isComponentIdIncludedInContextByFilter,
} from '@ardoq/component-interface';
import { resolveIconColor } from '../../componentDisplayColor';
import { componentOperations, modelOperations, toIds } from '@ardoq/core';
import { scopeDataOperations } from '@ardoq/scope-data';
import { fieldInterface } from '@ardoq/field-interface';
import { filterInterface } from '@ardoq/filter-interface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { isUndefinedOrNull, isUniqueCombination } from 'utils/collectionUtil';
import {
  bypassLimitAction,
  componentMatrixToggleCollapseGroup,
} from '../actions';
import {
  COMPONENT_HIERARCHY_PADDING,
  INCOMING_SUFFIX,
  LEVEL_HEADER_CELL_HORIZONTAL_PADDING,
  OUTGOING_SUFFIX,
} from '../consts';
import { COMPONENT_MATRIX_COMPONENTS_LIMIT } from 'views/environmentals/viewDataLimits';
import {
  ComponentMatrixDimension,
  ComponentMatrixGroup,
  ComponentMatrixGroupOwnerType,
  ComponentMatrixMeasurements,
  ComponentMatrixViewModel,
  ComponentMatrixViewProperties,
  ComponentMatrixViewSettings,
  ComponentMatrixViewStreamState,
  HeaderComponentData,
} from '../types';
import { getDivBackgroundColor } from '../util';
import { addAddressToGroup } from './addressUtil';
import {
  extractReferenceTypeId,
  extractWorkspaceId,
  formatReferenceType,
  getLinkedComponentsAddresses,
  referenceTypeIdMaxHierarchyDepth,
} from './referenceAddressUtil';
import { getRelevantComponentIds, HasViewInstanceId } from '@ardoq/graph';
import {
  type ExtractPayload,
  action$,
  combineReducers,
  dispatchAction,
  reducer,
  streamReducer,
} from '@ardoq/rxbeach';
import { context$ } from 'streams/context/context$';
import { modelUpdateNotification$ } from 'modelInterface/modelUpdateNotification$';
import { measureLevelLabel } from './measureLabels';
import { getRowModels, type GetRowModelsInputs } from './getRowModels';
import {
  getHeaderColumnWidths,
  measureCells,
  measureRowHeaders,
  measureRowLevelHeaders,
} from './measureCells';
import * as profiling from '@ardoq/profiling';
import {
  type TraversedReferenceTypeInfo,
  matchSelectedReferenceType,
  getReferenceTypeInfo,
} from '@ardoq/settings-bar';
import { debouncedCombineLatest } from 'streams/utils/streamUtils';
import {
  components$,
  ComponentsState,
  toComponentsState,
} from 'streams/components/components$';
import { workspaceAndModel$ } from 'streams/workspaceAndModel$';
import { paddedAddressPermutations } from './paddedAddressPermutations';

import {
  isReferenceIdIncludedInContextByFilter,
  referenceInterface,
} from '@ardoq/reference-interface';
import { ItemsType, nodeLimitError } from '@ardoq/error-info-box';
import { EMPTY_CONTEXT_SHAPE } from 'streams/context/ContextShape';
import {
  focusedComponentChanged,
  hoveredComponentChanged,
} from 'tabview/actions';
import { getWorkspaceModeViewSettings$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';
import { getDefaultViewSettings } from 'views/environmentals/viewSettings/getDefaultViewSettings';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale } from '@ardoq/i18n';
import {
  getErrorInfoBoxLabels,
  getNodeLimitErrorLabels,
} from 'locales/getErrorInfoBoxLabels';
import { locale$ } from 'streams/locale$';
import { ARDOQ_DEFAULT_LOCALE, formatNumber, type Locale } from '@ardoq/locale';
import {
  compareComponentsById,
  type ComponentByIdSortDependencies,
} from '@ardoq/common-helpers';
import { getComponentByIdSortDependencies } from 'modelInterface/components/getComponentByIdSortDependencies';

const getComponentType = (
  workspaceAndModel: WorkspaceAndModelLookup,
  component: APIComponentAttributes
): APIComponentType | null => {
  const result = scopeDataOperations.getWorkspaceAndModel(
    workspaceAndModel,
    component.rootWorkspace
  );
  if (!result) return null;
  return (
    modelOperations.getModelTypes(result.model).componentTypesById[
      component.typeId
    ] ?? null
  );
};

const buildRepresentationDataById = (
  componentsById: Record<ArdoqId, APIComponentAttributes>,
  workspaceAndModel: WorkspaceAndModelLookup
): Map<ArdoqId, RepresentationData> => {
  const representationDataById = new Map<ArdoqId, RepresentationData>();
  for (const component of Object.values(componentsById)) {
    // getRepresentationData short-circuits on component.image/icon before
    // consulting the type, so only resolve the type when it will be used.
    const componentType =
      component.image || component.icon
        ? null
        : getComponentType(workspaceAndModel, component);
    representationDataById.set(
      component._id,
      componentOperations.getRepresentationData(component, componentType)
    );
  }
  return representationDataById;
};

const buildCssClassNamesById = (
  componentIds: ArdoqId[]
): Map<ArdoqId, string> =>
  new Map(
    componentIds.map(componentId => [
      componentId,
      componentInterface.getCssClassNames(componentId, {
        useAsBackgroundStyle: true,
      }) ?? '',
    ])
  );

const buildIconColorById = (componentIds: ArdoqId[]): Map<ArdoqId, string> =>
  new Map(
    componentIds.map(componentId => [
      componentId,
      resolveIconColor(
        componentId,
        componentInterface.getComponentDisplayColorAsSVGAttributes
      ),
    ])
  );

type HalfAddress = (string | null)[];
interface Address {
  columnValues: HalfAddress;
  rowValues: HalfAddress;
}

const combineHalfAddresses = (
  fieldAddress: HalfAddress,
  referenceAddresses: HalfAddress[]
) =>
  referenceAddresses.length
    ? referenceAddresses.map(referenceAddress => [
        ...referenceAddress,
        ...fieldAddress,
      ])
    : [fieldAddress];

const initialStateMeasurements = () => ({
  mainContainerLeftColumnWidth: 0,
  columnHeaderWidths: [],
  columnHeaderAndColumnWidths: [],
  rowHeights: [],
});
const initialState: ComponentMatrixViewStreamState = {
  viewSettings: getDefaultViewSettings(ViewIds.COMPONENT_MATRIX, false),
  viewSettingsState: {
    listFields: [],
    traversedOutgoingReferenceTypes: [],
    traversedIncomingReferenceTypes: [],
    quickPerspectiveReferenceTypeFilters: null,
  },
  viewModel: {
    columns: new Map(),
    rows: new Map(),
    items: [],
    columnsByDepth: [],
    rowsByDepth: [],
    columnLevelLabels: [],
    rowLevelLabels: [],
    measurements: initialStateMeasurements(),
    rowModels: [],
    hoveredComponentId: null,
    focusedComponentId: null,
    contextComponentId: null,
    legendComponentTypes: [],
  },
  hasReferences: false,
  hasFilteredNullItems: false,
  errors: [],
  context: EMPTY_CONTEXT_SHAPE,
  components: toComponentsState([]),
  representationDataById: new Map(),
  cssClassNamesById: new Map(),
  iconColorById: new Map(),
  i18nLocale: DEFAULT_ARDOQ_I18NLOCALE,
  errorInfoBoxLabels: getErrorInfoBoxLabels(DEFAULT_ARDOQ_I18NLOCALE),
  userLocale: ARDOQ_DEFAULT_LOCALE,
  bypassLimit: false,
};

const EMPTY_GROUP_MAP = new Map<string | null, ComponentMatrixGroup>([
  [
    null,
    {
      address: [null],
      ownerType: ComponentMatrixGroupOwnerType.EMPTY,
      label: '',
      isExpanded: false,
      isHidden: false,
      children: new Map(),
      otherLabels: [],
    },
  ],
]);

const getFieldAddress = (
  componentId: ArdoqId,
  fieldNames: string[]
): (string | null)[] => {
  const component = componentInterface.get(componentId);
  return fieldNames.map(name => component?.[name] ?? null);
};

interface ViewModelAddresses {
  xFieldAddress: string[];
  xReferenceAddress: string | null;
  xReferenceTypeMaxHierarchyDepth: number;
  yFieldAddress: string[];
  yReferenceAddress: string | null;
  yReferenceTypeMaxHierarchyDepth: number;
  xReferenceIsOutgoing: boolean;
  yReferenceIsOutgoing: boolean;
}
interface ViewModelReducerState {
  viewModel: ComponentMatrixViewModel;
  addresses: ViewModelAddresses;
  addressFilter: (address: Address) => boolean;
}

// if address length is 0, we are showing everything in the "none" row or column.
const isEmptyOrHasNulls = (address: (string | null)[]) =>
  !address.length || address.some(isUndefinedOrNull);
const addressFilter =
  (filterNoneX: boolean, filterNoneY: boolean) => (address: Address) =>
    (!filterNoneX && !filterNoneY) ||
    ((!filterNoneX || !isEmptyOrHasNulls(address.columnValues)) &&
      (!filterNoneY || !isEmptyOrHasNulls(address.rowValues)));

const filterLinkedComponents =
  (getLinkedComponents: (componentId: string) => LinkedComponent[]) =>
  (componentId: string) =>
    getLinkedComponents(componentId).filter(linkedComponent =>
      isReferenceIdIncludedInContextByFilter(linkedComponent.referenceId)
    );
const getSources = filterLinkedComponents(componentInterface.getSources);
const getTargets = filterLinkedComponents(componentInterface.getTargets);

const viewModelReducer = (
  state: ViewModelReducerState,
  componentId: ArdoqId
): ViewModelReducerState => {
  const xFieldAddress = getFieldAddress(
    componentId,
    state.addresses.xFieldAddress
  );
  const xReferencesAddresses = state.addresses.xReferenceAddress
    ? getLinkedComponentsAddresses(
        componentId,
        extractReferenceTypeId(state.addresses.xReferenceAddress),
        extractWorkspaceId(state.addresses.xReferenceAddress),
        state.addresses.xReferenceIsOutgoing ? getTargets : getSources,
        true,
        state.addresses.xReferenceTypeMaxHierarchyDepth
      )
    : [];
  const yFieldAddress = getFieldAddress(
    componentId,
    state.addresses.yFieldAddress
  );
  const yReferencesAddresses = state.addresses.yReferenceAddress
    ? getLinkedComponentsAddresses(
        componentId,
        extractReferenceTypeId(state.addresses.yReferenceAddress),
        extractWorkspaceId(state.addresses.yReferenceAddress),
        state.addresses.yReferenceIsOutgoing ? getTargets : getSources,
        true,
        state.addresses.yReferenceTypeMaxHierarchyDepth
      )
    : [];

  const xData = {
    referencesAddresses: xReferencesAddresses,
    fieldAddress: xFieldAddress,
  };
  const yData = {
    referencesAddresses: yReferencesAddresses,
    fieldAddress: yFieldAddress,
  };

  const [xAddresses, yAddresses] = [xData, yData].map(
    ({ fieldAddress, referencesAddresses }) =>
      combineHalfAddresses(fieldAddress, referencesAddresses)
  );
  const [headerXAddresses, headerYAddresses] = [xData, yData].map(
    ({ fieldAddress, referencesAddresses }) => {
      // permuted reference addresses shouldn't have actual list values appended, otherwise this will create column headers for data that does not exist.
      const nullFieldAddress = range(fieldAddress.length).map(
        () => COMPONENT_HIERARCHY_PADDING
      );
      const referenceAddressPermutations =
        paddedAddressPermutations(referencesAddresses);
      return [
        ...combineHalfAddresses(fieldAddress, referencesAddresses),
        ...combineHalfAddresses(nullFieldAddress, referenceAddressPermutations),
      ];
    }
  );

  const [addresses, headerAddresses] = [
    [xAddresses, yAddresses],
    [headerXAddresses, headerYAddresses],
  ].map(([xAddresses, yAddresses]) =>
    yAddresses
      .flatMap(rowValues =>
        xAddresses.map(columnValues => ({
          columnValues,
          rowValues,
        }))
      )
      .filter(state.addressFilter)
  );
  const addressesToUse = addresses.filter(state.addressFilter);

  // #region add addresses, including padded address permutations, to the row and column maps
  headerAddresses.forEach(address => {
    const xFieldWidth = xFieldAddress.length;
    const xReferenceWidth = address.columnValues.length - xFieldWidth;
    const yFieldWidth = yFieldAddress.length;
    const yReferenceWidth = address.rowValues.length - yFieldWidth;

    const [xAddressOwnerTypes, yAddressOwnerTypes] = [
      [xReferenceWidth, xFieldWidth],
      [yReferenceWidth, yFieldWidth],
    ].map(([referenceWidth, fieldWidth]) => [
      ...range(referenceWidth).map(
        () => ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT
      ),
      ...range(fieldWidth).map(
        () => ComponentMatrixGroupOwnerType.LIST_FIELD_VALUE
      ),
    ]);

    const xAddressInfo = {
      address: address.columnValues,
      ownerTypes: xAddressOwnerTypes,
      groupMap: state.viewModel.columns,
    };
    const yAddressInfo = {
      address: address.rowValues,
      ownerTypes: yAddressOwnerTypes,
      groupMap: state.viewModel.rows,
    };
    [xAddressInfo, yAddressInfo].forEach(({ address, ownerTypes, groupMap }) =>
      addAddressToGroup(address, ownerTypes, groupMap)
    );
  });
  // #endregion

  /** all the row+column addresses for this component id. in other words, all the body cell locations of the component. */
  const componentAddresses = addressesToUse.map(address => ({
    componentId,
    address,
  }));
  componentAddresses.forEach(address => {
    state.viewModel.items.push(address);
  });
  return state;
};

type GroupLevelsState = {
  groupsByDepth: ComponentMatrixGroup[][];
  currentDepth: number;
};

const groupLevelsReducer = (
  state: GroupLevelsState,
  currentGroup: ComponentMatrixGroup
): GroupLevelsState => {
  if (state.groupsByDepth.length <= state.currentDepth) {
    state.groupsByDepth.push([]);
  }
  state.groupsByDepth[state.currentDepth].push(currentGroup);
  if (currentGroup.children.size) {
    [...currentGroup.children.values()].reduce(groupLevelsReducer, {
      ...state,
      currentDepth: state.currentDepth + 1,
    });
  }
  return state;
};

const listFieldLabel = (
  fieldName: string,
  listFields: APIFieldAttributes[]
) => {
  const field =
    fieldName &&
    listFields &&
    listFields.find(field => field.name === fieldName);
  if (!field) {
    return 'Unknown';
  }
  return field.label;
};

const groupLevelLabels = (
  referenceTypeName: string,
  maxDepth: number,
  isOutgoing: boolean
) => {
  if (!referenceTypeName) {
    return [];
  }

  const levelLabel = `${referenceTypeName}${
    isOutgoing ? OUTGOING_SUFFIX : INCOMING_SUFFIX
  }`;
  return maxDepth <= 1
    ? [levelLabel]
    : [levelLabel].concat(
        Array(maxDepth - 1).fill(COMPONENT_HIERARCHY_PADDING)
      );
};

const isComponentHierarchyPadding = (group: ComponentMatrixGroup) =>
  group.address[group.address.length - 1] === COMPONENT_HIERARCHY_PADDING;
const toggleExpandedGroups = (
  groups: Map<string | null, ComponentMatrixGroup>,
  [address, ...restAddress]: (string | null)[]
) => {
  const group = groups.get(address);
  if (group === undefined) return;

  const shouldToggle = restAddress.length === 0;
  if (shouldToggle) {
    // padding is always expanded, in case there's a list field inside
    group.isExpanded = !group.isExpanded || isComponentHierarchyPadding(group);

    group.children.forEach(child => setHidden(child, !group.isExpanded));

    if (group.isExpanded) {
      // all padding must be expanded.
      expandComponentHierarchyPadding(group);
    }
  } else {
    toggleExpandedGroups(group.children, restAddress);
  }
};
const setHidden = (group: ComponentMatrixGroup, hidden: boolean) => {
  group.isHidden = hidden;
  const iterateChildren = hidden || (!hidden && group.isExpanded); // hide children of hidden groups. show children of expanded groups.
  if (iterateChildren) {
    group.children.forEach(child => setHidden(child, hidden));
  }
};

const expandComponentHierarchyPadding = (group: ComponentMatrixGroup) => {
  [...group.children.values()]
    .filter(child => isComponentHierarchyPadding(child))
    .forEach(child => {
      child.isHidden = false;
      child.isExpanded = true;
      child.children.forEach(child => (child.isHidden = false));
      expandComponentHierarchyPadding(child);
    });
};

const getViewModel = (
  startSet: ArdoqId[],
  viewSettings: ComponentMatrixViewSettings,
  listFields: APIFieldAttributes[],
  traversedReferenceTypes: TraversedReferenceTypeInfo[],
  sortDependencies: ComponentByIdSortDependencies
) => {
  const selectedFieldNamesX = viewSettings.selectedFieldNamesX.filter(
    fieldName => listFields.some(field => field.name === fieldName)
  );
  const selectedFieldNamesY = viewSettings.selectedFieldNamesY.filter(
    fieldName => listFields.some(field => field.name === fieldName)
  );

  const { referenceTypeX, referenceTypeY } = viewSettings;
  const [foundReferenceTypeX, foundReferenceTypeY] = [
    referenceTypeX,
    referenceTypeY,
  ].map(referenceType =>
    referenceType === null
      ? null
      : (traversedReferenceTypes.find(traversedReferenceType =>
          matchSelectedReferenceType(referenceType, traversedReferenceType)
        ) ?? null)
  );

  const xReferenceAddress =
    foundReferenceTypeX === null
      ? ''
      : formatReferenceType(foundReferenceTypeX);
  const yReferenceAddress =
    foundReferenceTypeY === null
      ? ''
      : formatReferenceType(foundReferenceTypeY);

  const xReferenceTypeId = extractReferenceTypeId(xReferenceAddress);
  const yReferenceTypeId = extractReferenceTypeId(yReferenceAddress);
  const xReferenceWorkspace = extractWorkspaceId(xReferenceAddress);
  const yReferenceWorkspace = extractWorkspaceId(yReferenceAddress);

  const xReferenceTypeMaxHierarchyDepth = referenceTypeIdMaxHierarchyDepth(
    startSet,
    xReferenceTypeId,
    xReferenceWorkspace,
    viewSettings.referenceTypeX?.isOutgoing
      ? componentInterface.getTargets
      : componentInterface.getSources
  );
  const yReferenceTypeMaxHierarchyDepth = referenceTypeIdMaxHierarchyDepth(
    startSet,
    yReferenceTypeId,
    yReferenceWorkspace,
    viewSettings.referenceTypeY?.isOutgoing
      ? componentInterface.getTargets
      : componentInterface.getSources
  );

  const noColumns =
    xReferenceAddress.length === 0 && selectedFieldNamesX.length === 0;
  const noRows =
    yReferenceAddress.length === 0 && selectedFieldNamesY.length === 0;

  const viewModel = fillEmptyRowsOrColumns(
    startSet.reduce(viewModelReducer, {
      viewModel: {
        columns: new Map(),
        rows: new Map(),
        items: [],
        columnsByDepth: [],
        rowsByDepth: [],
        columnLevelLabels: [
          ...groupLevelLabels(
            foundReferenceTypeX?.displayName || '',
            xReferenceTypeMaxHierarchyDepth,
            Boolean(foundReferenceTypeX?.isOutgoing)
          ),
          ...selectedFieldNamesX.map(fieldName =>
            listFieldLabel(fieldName, listFields)
          ),
        ],
        rowLevelLabels: [
          ...groupLevelLabels(
            foundReferenceTypeY?.displayName || '',
            yReferenceTypeMaxHierarchyDepth,
            Boolean(foundReferenceTypeY?.isOutgoing)
          ),
          ...selectedFieldNamesY.map(fieldName =>
            listFieldLabel(fieldName, listFields)
          ),
        ],
        rowModels: [],
        measurements: initialStateMeasurements(),
        hoveredComponentId: null,
        focusedComponentId: null,
        contextComponentId: null,
        legendComponentTypes: [],
      },
      addresses: {
        xFieldAddress: selectedFieldNamesX,
        xReferenceAddress: xReferenceAddress,
        xReferenceTypeMaxHierarchyDepth,
        yFieldAddress: selectedFieldNamesY,
        yReferenceAddress: yReferenceAddress,
        yReferenceTypeMaxHierarchyDepth,
        xReferenceIsOutgoing: Boolean(foundReferenceTypeX?.isOutgoing),
        yReferenceIsOutgoing: Boolean(foundReferenceTypeY?.isOutgoing),
      },
      addressFilter: addressFilter(
        viewSettings.filterNulls && !noColumns,
        viewSettings.filterNulls && !noRows
      ),
    }).viewModel
  );

  const fieldByName = (fieldName: string) =>
    listFields.find(listField => listField.name === fieldName);
  const xFields = selectedFieldNamesX.map(fieldByName).filter(isTruthy);
  const yFields = selectedFieldNamesY.map(fieldByName).filter(isTruthy);

  viewModel.columns = sortGroups(viewModel.columns, xFields, sortDependencies);
  viewModel.rows = sortGroups(viewModel.rows, yFields, sortDependencies);
  viewModel.items = [...viewModel.items].sort((a, b) =>
    compareComponentsById(a.componentId, b.componentId, sortDependencies)
  );

  return viewModel;
};

const fillEmptyRowsOrColumns = (
  viewModel: ComponentMatrixViewModel
): ComponentMatrixViewModel => {
  const emptyRows = !viewModel.rows.size;
  const emptyColumns = !viewModel.columns.size;
  const rows = emptyRows ? EMPTY_GROUP_MAP : viewModel.rows;
  const columns = emptyColumns ? EMPTY_GROUP_MAP : viewModel.columns;
  const items =
    emptyRows && emptyColumns
      ? viewModel.items.map(item => ({
          ...item,
          address: {
            rowValues: [null],
            columnValues: [null],
          },
        }))
      : emptyRows
        ? viewModel.items.map(item => ({
            ...item,
            address: {
              rowValues: [null],
              columnValues: item.address.columnValues,
            },
          }))
        : emptyColumns
          ? viewModel.items.map(item => ({
              ...item,
              address: {
                rowValues: item.address.rowValues,
                columnValues: [null],
              },
            }))
          : viewModel.items;
  return { ...viewModel, rows, columns, items };
};

const groupKeyComparator =
  (
    listFieldOrder: string[] | undefined,
    sortDependencies: ComponentByIdSortDependencies
  ) =>
  (a: string | null, b: string | null) => {
    if (a === COMPONENT_HIERARCHY_PADDING) {
      return -1;
    }
    if (b === COMPONENT_HIERARCHY_PADDING) {
      return 1;
    }
    if (b === null) {
      return -1;
    }
    if (a === null) {
      return 1;
    }
    if (listFieldOrder) {
      return listFieldOrder.indexOf(a) - listFieldOrder.indexOf(b);
    }
    return compareComponentsById(a, b, sortDependencies);
  };

const sortGroups = (
  groups: Map<string | null, ComponentMatrixGroup>,
  listFields: APIFieldAttributes[],
  sortDependencies: ComponentByIdSortDependencies
): Map<string | null, ComponentMatrixGroup> => {
  const listField: APIFieldAttributes | undefined = listFields[0];
  const listFieldOrder = (listField?.defaultValue as string | undefined)?.split(
    ','
  );

  const sortedKeys = Array.from(groups.keys()).sort(
    groupKeyComparator(listFieldOrder, sortDependencies)
  );

  const sortedNonNullKeys = groups.has(null)
    ? [...sortedKeys.filter(key => key !== null), null] // "none" is last.
    : sortedKeys;

  return new Map(
    sortedNonNullKeys.map(key => {
      const group = groups.get(key)!;
      return [
        key,
        {
          ...group,
          children: sortGroups(
            group.children,
            listFields.slice(1),
            sortDependencies
          ),
        },
      ];
    })
  );
};

const getStartSet = (
  context: ContextShape,
  includeAllDescendants: boolean,
  components: ComponentsState
) => {
  const startSet = getRelevantComponentIds({
    componentId: context.componentId,
    workspaceId: context.workspaceId,
    childrenByParent: components.childrenByParent,
    discardFilteredChildren: true,
  });
  if (!includeAllDescendants) return startSet;
  const roots = startSet
    .map(componentId => components.byId[componentId])
    .filter(isTruthy);
  return toIds(
    componentOperations.getFlatHierarchy(roots, components.childrenByParent)
  );
};

const findDuplicates = <T>(
  collection: T[],
  isDuplicate: (itemA: T, itemB: T) => boolean
) =>
  collection.filter(
    (item, index, array) =>
      array.findIndex(itemB => isDuplicate(item, itemB)) !== index
  );

const resolveReferenceTypeInfo = (linkedComponent: LinkedComponent) =>
  getReferenceTypeInfo({
    referenceType: referenceInterface.getGlobalReferenceType(
      linkedComponent.referenceId
    ),
    linkedWorkspaceId:
      componentInterface.getWorkspaceId(linkedComponent.componentId) ?? '',
  });

const getReferenceTypes = (
  startSet: ArdoqId[],
  getSourcesOrTargets: (componentId: ArdoqId) => LinkedComponent[]
) => {
  const referenceTypes = uniqBy(
    startSet.flatMap(getSourcesOrTargets),
    'referenceId'
  )
    .map(resolveReferenceTypeInfo)
    .filter(isUniqueCombination('referenceTypeName', 'linkedWorkspaceId'));

  const duplicateReferenceTypeNames = new Set(
    findDuplicates(
      referenceTypes,
      (linkedComponentA, linkedComponentB) =>
        linkedComponentA.referenceTypeName ===
        linkedComponentB.referenceTypeName
    ).map(({ referenceTypeName }) => referenceTypeName)
  );

  return referenceTypes.map(referenceType => ({
    ...referenceType,
    displayName: duplicateReferenceTypeNames.has(
      referenceType.referenceTypeName
    )
      ? `${
          referenceType.referenceTypeName
        } (${workspaceInterface.getWorkspaceName(
          referenceType.linkedWorkspaceId
        )})`
      : referenceType.referenceTypeName,
  }));
};

const getReferenceTypeOptions = (startSet: ArdoqId[]) => ({
  traversedIncomingReferenceTypes: getReferenceTypes(
    startSet,
    componentInterface.getSources
  ).map(referenceType => ({ ...referenceType, isOutgoing: false })),
  traversedOutgoingReferenceTypes: getReferenceTypes(
    startSet,
    componentInterface.getTargets
  ).map(referenceType => ({ ...referenceType, isOutgoing: true })),
});

const getReferencesByComponentId = (
  componentId: ArdoqId
): APIReferenceAttributes[] => {
  const componentIds = [
    componentId,
    ...toIds(componentInterface.getSubtree(componentId)),
  ];

  return uniqBy(
    componentIds.flatMap(compId =>
      referenceInterface.getReferencesForComponent(compId)
    ),
    '_id'
  ).filter(ref => referenceInterface.isIncludedInContextByFilter(ref));
};

const getReferencesForSelectedContext = (
  context: ContextShape
): APIReferenceAttributes[] => {
  if (context.componentId) {
    return getReferencesByComponentId(context.componentId);
  }
  if (context.workspaceId) {
    return referenceInterface.getReferencesInWorkspace(context.workspaceId);
  }
  return [];
};

export const isSameComponentMatrixDataset = (
  a: ContextShape,
  b: ContextShape
): boolean =>
  a.workspaceId === b.workspaceId &&
  a.componentId === b.componentId &&
  a.scenarioId === b.scenarioId;

const reset = (
  viewSettings: ComponentMatrixViewSettings,
  context: ContextShape,
  bypassLimit: boolean,
  i18nLocale: I18nLocale,
  userLocale: Locale,
  components: ComponentsState,
  representationDataById: Map<ArdoqId, RepresentationData>
): ComponentMatrixViewStreamState => {
  const transaction = profiling.startTransaction(
    'component matrix reset view model',
    500,
    profiling.Team.INSIGHT
  );
  const fullStartSet = getStartSet(
    context,
    viewSettings.includeAllDescendants,
    components
  ).filter(componentId => isComponentIdIncludedInContextByFilter(componentId));

  const errors =
    fullStartSet.length > COMPONENT_MATRIX_COMPONENTS_LIMIT && !bypassLimit
      ? [
          nodeLimitError({
            itemCount: fullStartSet.length,
            limit: COMPONENT_MATRIX_COMPONENTS_LIMIT,
            itemsType: ItemsType.COMPONENTS,
            formatNumber: value => formatNumber(value, 'default', userLocale),
            onProceedAnyway: () => dispatchAction(bypassLimitAction()),
            ...getNodeLimitErrorLabels(i18nLocale),
          }),
        ]
      : [];

  const startSet = bypassLimit
    ? fullStartSet
    : fullStartSet.slice(0, COMPONENT_MATRIX_COMPONENTS_LIMIT);

  const { traversedIncomingReferenceTypes, traversedOutgoingReferenceTypes } =
    getReferenceTypeOptions(startSet);

  const listFields = fieldInterface.getFieldsOfWorkspace(context.workspaceId, [
    APIFieldType.LIST,
  ]);

  const hasReferences = !!getReferencesForSelectedContext(context).length;

  const cssClassNamesById = buildCssClassNamesById(startSet);

  const iconColorById = buildIconColorById(startSet);

  const sortDependencies = getComponentByIdSortDependencies({
    componentIds: startSet,
    sort: context.sort,
    userLocale,
  });

  const viewModel = {
    ...augmentViewModel(
      getViewModel(
        startSet,
        viewSettings,
        listFields,
        [
          ...traversedIncomingReferenceTypes,
          ...traversedOutgoingReferenceTypes,
        ],
        sortDependencies
      ),
      {
        contextComponentId: context.componentId,
        representationDataById,
        cssClassNamesById,
        iconColorById,
      }
    ),
    contextComponentId: context.componentId || null,
  };

  const hasFilteredNullItems =
    viewSettings.filterNulls && startSet.length !== viewModel.items.length;

  const result = {
    viewSettingsState: {
      listFields,
      traversedIncomingReferenceTypes,
      traversedOutgoingReferenceTypes,
      quickPerspectiveReferenceTypeFilters:
        filterInterface.getActiveQuickReferenceTypeFilters(),
    },
    viewSettings,
    context,
    components,
    representationDataById,
    cssClassNamesById,
    iconColorById,
    viewModel,
    hasReferences,
    hasFilteredNullItems,
    errors,
    i18nLocale,
    errorInfoBoxLabels: getErrorInfoBoxLabels(i18nLocale),
    userLocale,
    bypassLimit,
  };
  profiling.endTransaction(transaction, {
    viewId: ViewIds.COMPONENT_MATRIX,
    metadata: { itemCount: result.viewModel.items.length },
  });
  return result;
};
const cloneGroup = (group: ComponentMatrixGroup): ComponentMatrixGroup => ({
  ...group,
  children: cloneMap(group.children, cloneGroup),
});
const cloneMap = <K, V>(
  target: Map<K, V>,
  cloneValue: (value: V) => V = value => Object.assign({}, value)
) =>
  new Map(
    [...target.entries()].map(([key, value]) => [key, cloneValue(value)])
  );

const reduceCollapseGroup = (
  state: ComponentMatrixViewStreamState,
  {
    address,
    dimension,
  }: ExtractPayload<typeof componentMatrixToggleCollapseGroup>
): ComponentMatrixViewStreamState => {
  const newViewModel = { ...state.viewModel };
  if (dimension === ComponentMatrixDimension.COLUMN) {
    const newColumns = cloneMap(newViewModel.columns, cloneGroup);
    toggleExpandedGroups(newColumns, address);
    newViewModel.columns = newColumns;
  } else if (dimension === ComponentMatrixDimension.ROW) {
    const newRows = cloneMap(newViewModel.rows, cloneGroup);
    toggleExpandedGroups(newRows, address);
    newViewModel.rows = newRows;
  }
  return {
    ...state,
    viewModel: augmentViewModel(newViewModel, {
      contextComponentId: state.context.componentId,
      representationDataById: state.representationDataById,
      cssClassNamesById: state.cssClassNamesById,
      iconColorById: state.iconColorById,
    }),
  };
};
const toggleCollapseGroup = reducer(
  componentMatrixToggleCollapseGroup,
  reduceCollapseGroup
);

const handleHoveredComponentChanged = (
  state: ComponentMatrixViewStreamState,
  hoveredComponentId: ArdoqId | null
): ComponentMatrixViewStreamState => ({
  ...state,
  viewModel: {
    ...state.viewModel,
    hoveredComponentId,
  },
});

const handleFocusedComponentChanged = (
  state: ComponentMatrixViewStreamState,
  focusedComponentId: ArdoqId | null
): ComponentMatrixViewStreamState => ({
  ...state,
  viewModel: {
    ...state.viewModel,
    focusedComponentId:
      focusedComponentId !== state.viewModel.focusedComponentId
        ? focusedComponentId
        : null,
    hoveredComponentId: null,
  },
});

const measure = (
  viewModel: ComponentMatrixViewModel
): ComponentMatrixMeasurements => {
  const { rowModels } = viewModel;
  const columnLevelLabelWidths =
    viewModel.columnLevelLabels.map(measureLevelLabel);
  const widestColumnLevelLabel =
    columnLevelLabelWidths.length === 0
      ? 0
      : Math.max(...columnLevelLabelWidths);

  const cellSizes = measureCells(rowModels);
  const rowHeaderSizes = measureRowHeaders(rowModels);

  const columnWidths =
    cellSizes.length === 0
      ? []
      : range(cellSizes[0].length).map(columnIndex =>
          Math.max(...cellSizes.map(row => row[columnIndex].width))
        );

  const columnHeaderWidths = getHeaderColumnWidths(viewModel);
  const columnHeaderAndColumnWidths = columnHeaderWidths.map(
    (columnHeaderWidth, index) =>
      Math.max(columnHeaderWidth, columnWidths[index])
  );

  const rowBodyHeights = cellSizes.map(row =>
    Math.max(...row.map(cell => cell.height))
  );
  const rowHeaderHeights = rowHeaderSizes.map(row => row.height);

  const rowHeights = rowBodyHeights.map((rowBodyHeight, index) =>
    Math.max(rowBodyHeight, rowHeaderHeights[index])
  );

  const rowHeadersAreaWidth = Math.max(
    ...rowHeaderSizes.map(rowHeaderSize => rowHeaderSize.width)
  );

  const levelHeadersAreaLeftColumnWidth = measureRowLevelHeaders(
    viewModel.rowLevelLabels
  );

  const levelHeadersAreaRightColumnWidth =
    widestColumnLevelLabel === 0
      ? 0
      : widestColumnLevelLabel + 2 * LEVEL_HEADER_CELL_HORIZONTAL_PADDING;

  const mainContainerLeftColumnWidth = Math.max(
    levelHeadersAreaLeftColumnWidth,
    levelHeadersAreaRightColumnWidth,
    rowHeadersAreaWidth
  );

  return {
    mainContainerLeftColumnWidth,
    columnHeaderWidths,
    columnHeaderAndColumnWidths,
    rowHeights,
  };
};

const getGroupsByDepth = (groups: ComponentMatrixGroup[]) =>
  groups.reduce(groupLevelsReducer, {
    groupsByDepth: [],
    currentDepth: 0,
  }).groupsByDepth;
const augmentWithGroupsByDepth = (viewModel: ComponentMatrixViewModel) => ({
  ...viewModel,
  columnsByDepth: getGroupsByDepth([...viewModel.columns.values()]),
  rowsByDepth: getGroupsByDepth([...viewModel.rows.values()]),
});
const EMPTY_HEADER_REPRESENTATION_DATA: RepresentationData = {
  isImage: false,
  value: null,
};

const getGroupBackgroundColor = (
  componentId: string | null,
  cssClassNamesById: Map<ArdoqId, string>
): string =>
  getDivBackgroundColor(
    componentId
      ? (cssClassNamesById.get(componentId) ??
          componentInterface.getCssClassNames(componentId, {
            useAsBackgroundStyle: true,
          }) ??
          '')
      : ''
  );

/** counts the number of consecutive ancestors which are the same color as the component with the given address. */
const headerBrightnessIncrease = (
  color: string,
  address: (string | null)[],
  cssClassNamesById: Map<ArdoqId, string>
): number => {
  if (address.length <= 1) {
    return 0;
  }
  const parentId = address[address.length - 2];
  if (!parentId) {
    return 0;
  }
  const parentColor = getGroupBackgroundColor(parentId, cssClassNamesById);
  if (parentColor !== color) {
    return 0;
  }
  return (
    1 +
    headerBrightnessIncrease(
      parentColor,
      address.slice(0, -1),
      cssClassNamesById
    )
  );
};

const getHeaderComponentData = (
  componentId: ArdoqId,
  address: (string | null)[],
  {
    contextComponentId,
    representationDataById,
    cssClassNamesById,
  }: GetRowModelsInputs
): HeaderComponentData => {
  const className =
    cssClassNamesById.get(componentId) ??
    componentInterface.getCssClassNames(componentId, {
      useAsBackgroundStyle: true,
    }) ??
    '';
  return {
    className,
    representationData:
      representationDataById.get(componentId) ??
      componentInterface.getRepresentationData(componentId) ??
      EMPTY_HEADER_REPRESENTATION_DATA,
    isContext: componentId === contextComponentId,
    brightnessIncreaseFactor: headerBrightnessIncrease(
      getDivBackgroundColor(className),
      address,
      cssClassNamesById
    ),
    iconColor: resolveIconColor(
      componentId,
      componentInterface.getComponentDisplayColorAsSVGAttributes
    ),
  };
};

const augmentGroupWithHeaderComponentData = (
  group: ComponentMatrixGroup,
  rowModelInputs: GetRowModelsInputs
): ComponentMatrixGroup => {
  const componentId =
    group.ownerType === ComponentMatrixGroupOwnerType.REFERENCED_COMPONENT
      ? group.address[group.address.length - 1]
      : null;
  return {
    ...group,
    headerComponentData: componentId
      ? getHeaderComponentData(componentId, group.address, rowModelInputs)
      : undefined,
    children: augmentGroupMapWithHeaderComponentData(
      group.children,
      rowModelInputs
    ),
  };
};

const augmentGroupMapWithHeaderComponentData = (
  groups: Map<string | null, ComponentMatrixGroup>,
  rowModelInputs: GetRowModelsInputs
): Map<string | null, ComponentMatrixGroup> =>
  new Map(
    [...groups.entries()].map(([key, group]) => [
      key,
      augmentGroupWithHeaderComponentData(group, rowModelInputs),
    ])
  );

const augmentWithHeaderComponentData = (
  viewModel: ComponentMatrixViewModel,
  rowModelInputs: GetRowModelsInputs
): ComponentMatrixViewModel => ({
  ...viewModel,
  columns: augmentGroupMapWithHeaderComponentData(
    viewModel.columns,
    rowModelInputs
  ),
  rows: augmentGroupMapWithHeaderComponentData(viewModel.rows, rowModelInputs),
});

const augmentViewModel = (
  viewModel: ComponentMatrixViewModel,
  rowModelInputs: GetRowModelsInputs
) =>
  augmentWithMeasurements(
    augmentWithRowModels(
      augmentWithGroupsByDepth(
        augmentWithHeaderComponentData(viewModel, rowModelInputs)
      ),
      rowModelInputs
    )
  );
const augmentWithRowModels = (
  viewModel: ComponentMatrixViewModel,
  rowModelInputs: GetRowModelsInputs
) => ({
  ...viewModel,
  rowModels: getRowModels(viewModel, rowModelInputs),
  legendComponentTypes: computeLegendComponentTypes(viewModel.items),
});
const augmentWithMeasurements = (viewModel: ComponentMatrixViewModel) => ({
  ...viewModel,
  measurements: measure(viewModel),
});

export const getViewModel$ = ({
  viewInstanceId,
}: HasViewInstanceId): Observable<ComponentMatrixViewProperties> => {
  const resetFromStream = (
    previousState: ComponentMatrixViewStreamState,
    [
      viewSettings,
      context,
      components,
      workspaceAndModel,
      i18nLocale,
      locale,
    ]: [
      ComponentMatrixViewSettings,
      ContextShape,
      ComponentsState,
      WorkspaceAndModelLookup,
      I18nLocale,
      Locale,
      null,
    ]
  ) =>
    reset(
      viewSettings,
      context,
      isSameComponentMatrixDataset(context, previousState.context)
        ? previousState.bypassLimit
        : false,
      i18nLocale,
      locale,
      components,
      buildRepresentationDataById(components.byId, workspaceAndModel)
    );

  const resetReducer = streamReducer(
    debouncedCombineLatest([
      getWorkspaceModeViewSettings$(
        ViewIds.COMPONENT_MATRIX,
        viewInstanceId
      ).pipe(distinctUntilChanged((a, b) => isEqual(a, b))),
      context$,
      components$,
      workspaceAndModel$,
      i18nLocale$,
      locale$,
      modelUpdateNotification$,
    ]),
    resetFromStream
  );

  const handleBypassLimit = (state: ComponentMatrixViewStreamState) =>
    reset(
      state.viewSettings,
      state.context,
      true,
      state.i18nLocale,
      state.userLocale,
      state.components,
      state.representationDataById
    );

  const state$ = action$.pipe(
    combineReducers<ComponentMatrixViewStreamState>(initialState, [
      toggleCollapseGroup,
      resetReducer,
      reducer(hoveredComponentChanged, handleHoveredComponentChanged),
      reducer(focusedComponentChanged, handleFocusedComponentChanged),
      reducer(bypassLimitAction, handleBypassLimit),
    ])
  );

  return combineLatest([state$, i18nLocale$]).pipe(
    map(([state, i18nLocale]) => ({
      ...state,
      i18nLocale,
      errorInfoBoxLabels: getErrorInfoBoxLabels(i18nLocale),
    }))
  );
};
